// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared hook for fetching Digital Twin site data.
 * Used by DCAssets and KnowledgeGraph pages.
 *
 * Follows the proven fetch pattern from FleetView.tsx.
 */

import { useState, useEffect, useCallback } from 'react';
import { getDatacenterDetails } from '../services/digitalTwinService';
import type { Entity, Relationship } from '../types/digitalTwin';

/** Poll live telemetry (entity state) once per minute. */
export const TELEMETRY_POLL_INTERVAL_MS = 60_000;

export interface UseDigitalTwinOptions {
  /**
   * When set, silently re-fetches the site graph on this interval (ms) to
   * refresh live telemetry/state. Background refreshes never toggle `loading`
   * or clear existing data on failure, so the UI stays put between updates.
   */
  pollIntervalMs?: number;
}

export interface UseDigitalTwinResult {
  entities: Entity[];
  relationships: Relationship[];
  siteId: string;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDigitalTwin(
  siteId: string | undefined,
  options: UseDigitalTwinOptions = {}
): UseDigitalTwinResult {
  const { pollIntervalMs } = options;
  const [entities, setEntities] = useState<Entity[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [resolvedSiteId, setResolvedSiteId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (id: string, signal: { cancelled: boolean }, silent = false) => {
      try {
        // A silent poll refreshes telemetry in the background: keep the current
        // graph on screen (no spinner) and preserve it if the request fails.
        if (!silent) {
          setLoading(true);
          setError(null);
        }

        const allEntities: Entity[] = [];
        const allRelationships: Relationship[] = [];
        let resolvedId = '';
        let cursor: string | undefined;

        // Follow cursor-based pagination until the API stops returning a
        // nextCursor — large sites can have thousands of entities spread
        // across many pages (page size is capped upstream), so a single
        // request only ever returns a partial graph.
        do {
          const data = await getDatacenterDetails(id, { limit: 5000, cursor });
          if (signal.cancelled) return;
          resolvedId = data.siteId;
          allEntities.push(...(data.entities ?? []));
          allRelationships.push(...(data.relationships ?? []));
          cursor = data.page?.nextCursor;
        } while (cursor);

        setResolvedSiteId(resolvedId);
        setEntities(allEntities);
        setRelationships(allRelationships);
      } catch (err) {
        if (signal.cancelled) return;
        // Don't surface transient polling failures: keep the last good graph on
        // screen rather than flipping the whole panel to an error state.
        if (silent) {
          console.warn('Digital Twin telemetry poll failed:', err);
        } else {
          setError((err as Error).message ?? 'Failed to load site data');
        }
      } finally {
        if (!silent && !signal.cancelled) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!siteId) {
      setLoading(false);
      return;
    }
    const signal = { cancelled: false };
    fetchData(siteId, signal);
    return () => {
      signal.cancelled = true;
    };
  }, [siteId, fetchData]);

  // Poll live telemetry (entity state) on an interval when enabled. Each tick
  // is a silent background refresh so the graph and telemetry panels update in
  // place without a loading flash.
  useEffect(() => {
    if (!siteId || !pollIntervalMs) return;
    const signal = { cancelled: false };
    const intervalId = setInterval(() => {
      fetchData(siteId, signal, true);
    }, pollIntervalMs);
    return () => {
      signal.cancelled = true;
      clearInterval(intervalId);
    };
  }, [siteId, pollIntervalMs, fetchData]);

  const refetch = useCallback(() => {
    if (siteId) {
      const signal = { cancelled: false };
      fetchData(siteId, signal);
    }
  }, [siteId, fetchData]);

  return {
    entities,
    relationships,
    siteId: resolvedSiteId,
    loading,
    error,
    refetch,
  };
}
